/**
 * Sequence email step: template merge fields set in the step editor (2026-10-30).
 *
 * Stored in message_sequence_steps.action_config.email_template_data and passed to the SendGrid template
 * on every send of the step (process-sequence-steps buildSequenceEmailTemplateData). Three managed keys:
 *   landing_page_link -> {{landing_page_link}} (the button link)
 *   preheader         -> {{preheader}}
 *   subject           -> {{subject}} (only when "use this subject in the template" is ticked; templates
 *                        written as {{#if subject}}{{{subject}}}{{else}}...{{/if}} fall back without it)
 * Any other key already in email_template_data is preserved untouched.
 *
 * Must mirror the DB CHECK message_sequence_steps_template_data_chk (migration 1311) and
 * isValidSequenceLandingLink in supabase/functions/process-sequence-steps/index.ts.
 */

export const LANDING_LINK_MAX = 2000;
export const TEMPLATE_TEXT_MAX = 500;
// Same rule as the DB CHECK (mig 1311) and process-sequence-steps: https; host = dot-separated labels ending in a
// 2-63 letter TLD, optional port; no credentials; no whitespace/quotes/angle brackets/braces.
const LANDING_LINK_RE =
  /^https:\/\/(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}(?::[0-9]{1,5})?(?:[/?#][^\s<>"'{}`\\]*)?$/;

export interface StepTemplateFields {
  landingPageLink: string;
  preheader: string;
  passSubject: boolean;
}

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/** Read the editor fields from a step's stored email_template_data. */
export function readStepTemplateFields(
  emailTemplateData: unknown,
): StepTemplateFields {
  const d = asObject(emailTemplateData);
  return {
    landingPageLink:
      typeof d.landing_page_link === "string" ? d.landing_page_link : "",
    preheader: typeof d.preheader === "string" ? d.preheader : "",
    passSubject: typeof d.subject === "string" && d.subject.trim() !== "",
  };
}

/** Length in Unicode code points, as Postgres length() counts it (String.length counts UTF-16 units). */
export function codePointLength(value: string): number {
  return Array.from(value).length;
}

/** null when OK (including empty = no link), else an operator-facing error. */
export function validateLandingPageLink(raw: string): string | null {
  const v = raw.trim();
  if (v === "") {
    return null;
  }
  if (!/^https:\/\//.test(v)) {
    return "The landing page link must start with https://";
  }
  if (codePointLength(v) > LANDING_LINK_MAX) {
    return `The landing page link is too long (max ${LANDING_LINK_MAX} characters).`;
  }
  if (/[{}]/.test(v)) {
    return "The landing page link can't contain {{merge fields}}. Paste the full, final URL.";
  }
  if (/^https:\/\/[^/?#]*@/.test(v)) {
    return "The landing page link can't contain a username or password.";
  }
  if (!LANDING_LINK_RE.test(v)) {
    return "The landing page link isn't a valid URL. Use a full address like https://www.example.com/page (no spaces, quotes or angle brackets).";
  }
  return null;
}

export function validatePreheader(raw: string): string | null {
  return codePointLength(raw) > TEMPLATE_TEXT_MAX
    ? `The preheader is too long (max ${TEMPLATE_TEXT_MAX} characters).`
    : null;
}

/**
 * Only checked when "Use this step's Email Subject inside the template" is ticked: the subject is then sent as
 * {{subject}}. Braces would be shown literally (SendGrid does not re-evaluate data), which almost always means
 * an operator tried to use a merge field there.
 */
export function validateSubjectForTemplate(
  subject: string,
  passSubject: boolean,
): string | null {
  if (!passSubject) {
    return null;
  }
  if (/[{}]/.test(subject)) {
    return "The Email Subject can't contain { or } when it is used inside the template. Remove the braces or untick the option.";
  }
  if (codePointLength(subject.trim()) > TEMPLATE_TEXT_MAX) {
    return `The Email Subject is too long to use inside the template (max ${TEMPLATE_TEXT_MAX} characters).`;
  }
  return null;
}

/**
 * Merge the editor fields into the stored email_template_data. Returns undefined when nothing is left,
 * so a step that never used these fields saves exactly as before (no email_template_data key).
 */
export function buildStepTemplateData(
  existing: unknown,
  fields: StepTemplateFields,
  emailSubject: string,
): Record<string, unknown> | undefined {
  const out: Record<string, unknown> = { ...asObject(existing) };
  delete out.landing_page_link;
  delete out.preheader;
  delete out.subject;

  const link = fields.landingPageLink.trim();
  if (link !== "") {
    out.landing_page_link = link;
  }
  if (fields.preheader.trim() !== "") {
    out.preheader = fields.preheader;
  }
  const subject = emailSubject.trim();
  if (fields.passSubject && subject !== "") {
    out.subject = subject;
  }

  return Object.keys(out).length > 0 ? out : undefined;
}
